import { useCallback, useMemo } from 'react'
import { renderAgentMarkdown } from '@/components/agent-markdown/renderAgentMarkdown'
import { useGeneralAgentChatStore } from '../hooks/generalAgentChatStore'
import type { GeneralChatMessage } from '../types'
import { threadLoadsOf, threadOf } from './results/cardResults'
import { useThreadCards } from './results/cardResultsStore'
import { ChatCardView } from './results/ChatCardView'
import { threadInvoicesOf, ThreadInvoicesProvider } from './results/threadInvoices'

interface GeneralAgentMessageListProps {
    messages: GeneralChatMessage[]
    waiting: boolean
}

export const GeneralAgentMessageList = ({ messages, waiting }: GeneralAgentMessageListProps) => {
    const tabId = useGeneralAgentChatStore((s) => s.tabId)
    const send = useGeneralAgentChatStore((s) => s.send)
    // A card's rows are drawn where its result came, between the user's message and the reply that sums it up (FR-G3).
    const cards = useThreadCards(tabId, messages)
    const thread = useMemo(() => threadOf(messages, cards), [messages, cards])
    const loads = useMemo(() => threadLoadsOf(cards), [cards])
    // A load the chat invoiced reads as invoiced on every card, the ones drawn before the invoice too.
    const invoiced = useMemo(() => threadInvoicesOf(cards), [cards])
    // Stable, so a card already drawn is not drawn again on every render of the list.
    const onSend = useCallback((message: string) => void send(message), [send])

    if (!messages.length && !waiting) {
        return <p className="m-auto text-sm text-foreground-muted">Ask anything to start.</p>
    }
    return (
        <ThreadInvoicesProvider invoiced={invoiced}>
            <ol className="flex flex-col gap-3" aria-live="polite">
                {/* No AI Hub load table here, so replies keep `#N` as plain text (no `loadRefJump`). */}
                {thread.map((item) => {
                    if (item.kind === 'card') {
                        return (
                            <li key={item.card.id} className="flex">
                                <ChatCardView card={item.card} loads={loads} onSend={onSend} />
                            </li>
                        )
                    }
                    const m = item.message
                    const mine = m.message_type === 'user'
                    return (
                        <li
                            key={m.id}
                            className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm ${
                                mine ? 'self-end whitespace-pre-wrap bg-accent text-accent-foreground' : 'self-start bg-default text-default-foreground'
                            }`}
                        >
                            {mine ? m.content : renderAgentMarkdown(m.content ?? '', { links: true })}
                        </li>
                    )
                })}
            </ol>
        </ThreadInvoicesProvider>
    )
}
